import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCheck, Sparkles, MessageCircle, CreditCard, Inbox } from "lucide-react";

const TYPE_ICON = {
  program_assigned: Sparkles,
  message_from_coach: MessageCircle,
  message_from_athlete: MessageCircle,
  new_subscription: CreditCard,
};

const TYPE_COLOR = {
  program_assigned: "#d7ff00",
  message_from_coach: "#71c4ff",
  message_from_athlete: "#71c4ff",
  new_subscription: "#d7ff00",
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function NotificationBell({ variant = "athlete" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const loadCount = () => {
    api.get("/notifications/unread-count").then(r => setUnread(r.data.count || 0)).catch(() => {});
  };
  const loadItems = () => {
    api.get("/notifications?limit=20").then(r => setItems(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = () => {
    if (!open) loadItems();
    setOpen(!open);
  };

  const handleClick = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
    setItems(items.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
    setUnread(Math.max(0, unread - (n.read_at ? 0 : 1)));
    setOpen(false);
    if (n.link) {
      try {
        const url = new URL(n.link, window.location.origin);
        navigate(url.pathname + url.search + url.hash);
      } catch {
        navigate(n.link);
      }
    }
  };

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* noop */ }
    setItems(items.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={toggle}
        className="relative p-2 text-[#888] hover:text-white transition-colors"
        data-testid={`${variant}-notification-bell`}
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#d7ff00] text-black text-[9px] font-bold flex items-center justify-center font-mono-jp"
            data-testid="notification-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[340px] sm:w-[380px] max-h-[480px] bg-[#0a0a0a] border border-[#d7ff00]/40 shadow-[0_8px_40px_rgba(0,0,0,0.6)] z-50 flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1f1f1f]">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="font-mono-jp text-[9px] uppercase tracking-widest text-[#888] hover:text-[#d7ff00] flex items-center gap-1"
                data-testid="mark-all-read"
              >
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Inbox className="w-8 h-8 text-[#333] mx-auto mb-2" />
                <div className="text-[#888] text-xs">No notifications yet.</div>
                <div className="text-[#555] text-[10px] mt-1 font-mono-jp uppercase tracking-widest">You're all caught up.</div>
              </div>
            ) : (
              items.map((n) => {
                const Icon = TYPE_ICON[n.type] || Bell;
                const color = TYPE_COLOR[n.type] || "#888";
                const isUnread = !n.read_at;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex gap-3 border-b border-[#141414] last:border-b-0 hover:bg-[#0e0e0e] transition-colors ${isUnread ? "bg-[#0e0e0e]/60" : ""}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${color}22`, border: `1px solid ${color}66` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color }} />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`text-sm font-medium truncate ${isUnread ? "text-white" : "text-[#bbb]"}`}>{n.title}</div>
                        {isUnread && <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#d7ff00] mt-1.5" />}
                      </div>
                      <div className="text-[11px] text-[#888] mt-0.5 line-clamp-2">{n.body}</div>
                      <div className="text-[9px] text-[#555] font-mono-jp uppercase tracking-widest mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
